import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { getAllSessions, getScheduleRules } from '@/lib/api';
import type { LiveSession, ScheduleRule } from '@/types';
import AddRoomForm from '@/components/AddRoomForm';
import DownloadDataDialog from '@/components/DownloadDataDialog';
import LiveFocusPanel from '@/components/LiveFocusPanel';
import RealtimeStatusPanel from '@/components/RealtimeStatusPanel';
import RecentSessionsList from '@/components/RecentSessionsList';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { useRealtimeSessions } from '@/hooks/useRealtimeSession';

export default function MonitorPage() {
  const [sessions, setSessions] = useState<LiveSession[]>([]);
  const [scheduleRules, setScheduleRules] = useState<ScheduleRule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const [refreshNonce, setRefreshNonce] = useState(0);

  const { newSession, updatedSession } = useRealtimeSessions();

  const loadDashboard = useCallback(async (showInitialLoading = false) => {
    if (showInitialLoading) setIsLoading(true);
    else setIsRefreshing(true);
    setError(null);

    const [sessionResult, ruleResult] = await Promise.allSettled([
      getAllSessions(),
      getScheduleRules(),
    ]);

    if (sessionResult.status === 'fulfilled') {
      setSessions(sessionResult.value);
    } else {
      setError('场次数据暂时无法加载，请稍后重试');
    }

    if (ruleResult.status === 'fulfilled') {
      setScheduleRules(ruleResult.value);
    } else {
      setScheduleRules([]);
    }

    setIsLoading(false);
    setIsRefreshing(false);
    setLastUpdatedAt(new Date());
  }, []);

  useEffect(() => {
    void loadDashboard(true);
  }, [loadDashboard]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      void loadDashboard(false);
    }, 30_000);
    return () => window.clearInterval(intervalId);
  }, [loadDashboard]);

  useEffect(() => {
    if (!newSession) return;
    setSessions((previous) =>
      previous.some((session) => session.id === newSession.id)
        ? previous
        : [newSession, ...previous]
    );
  }, [newSession]);

  useEffect(() => {
    if (!updatedSession) return;
    setSessions((previous) => {
      const exists = previous.some((session) => session.id === updatedSession.id);
      return exists
        ? previous.map((session) => (session.id === updatedSession.id ? updatedSession : session))
        : [updatedSession, ...previous];
    });
  }, [updatedSession]);

  const activeSession = useMemo(
    () =>
      sessions.find((session) => session.status === 'live' && session.is_recording) ??
      sessions.find((session) => session.status === 'live') ??
      null,
    [sessions]
  );

  const nextSession = useMemo(() => {
    const now = Date.now();
    return [...sessions]
      .filter((session) => {
        if (session.status !== 'scheduled' || !session.scheduled_start_time) return false;
        const timestamp = new Date(session.scheduled_start_time).getTime();
        return !Number.isNaN(timestamp) && timestamp >= now;
      })
      .sort(
        (left, right) =>
          new Date(left.scheduled_start_time as string).getTime() -
          new Date(right.scheduled_start_time as string).getTime()
      )[0] ?? null;
  }, [sessions]);

  const nextRule = useMemo(() => {
    const now = Date.now();
    return [...scheduleRules]
      .filter((rule) => {
        if (!rule.is_enabled || !rule.next_trigger_at) return false;
        const timestamp = new Date(rule.next_trigger_at).getTime();
        return !Number.isNaN(timestamp) && timestamp >= now;
      })
      .sort(
        (left, right) =>
          new Date(left.next_trigger_at as string).getTime() -
          new Date(right.next_trigger_at as string).getTime()
      )[0] ?? null;
  }, [scheduleRules]);

  const handleSessionAdded = () => {
    setIsAddDialogOpen(false);
    void loadDashboard(false);
  };

  const handleSessionUpdated = () => {
    void loadDashboard(false);
  };

  const handleRefresh = () => {
    setRefreshNonce((value) => value + 1);
    void loadDashboard(false);
  };

  return (
    <div className="console-page">
      <header className="console-page-header">
        <div>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="console-title mt-0">直播监控</h1>
            <p className="font-mono text-[11px] text-[var(--text-muted)] md:text-xs">
              更新于 {lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString('zh-CN') : '--:--:--'} · 自动刷新 30s
            </p>
          </div>
        </div>

        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <Button
            type="button"
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            刷新数据
          </Button>
          <DownloadDataDialog />
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="col-span-2 min-h-11 sm:col-span-1">
                <Plus className="h-4 w-4" />
                创建新场次
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>创建直播场次</DialogTitle>
                <DialogDescription>输入 B 站直播间链接，并选择立即采集或预约开始时间。</DialogDescription>
              </DialogHeader>
              <AddRoomForm onSessionAdded={handleSessionAdded} />
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {error && (
        <div role="alert" className="console-alert-error px-4 py-3">
          <p className="text-sm">{error}</p>
        </div>
      )}

      <RealtimeStatusPanel sessions={sessions} refreshNonce={refreshNonce} />

      {isLoading ? (
        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.55fr)_minmax(300px,0.7fr)]" aria-label="正在加载直播场次">
          <div className="h-[252px] animate-pulse rounded-lg border border-[var(--structural-border)] bg-[var(--panel-white)]" />
          <div className="h-[252px] animate-pulse rounded-lg border border-[var(--structural-border)] bg-[var(--panel-white)]" />
        </div>
      ) : (
        <LiveFocusPanel
          activeSession={activeSession}
          nextSession={nextSession}
          nextRule={nextRule}
          onSessionUpdated={handleSessionUpdated}
          onCreateSession={() => setIsAddDialogOpen(true)}
        />
      )}

      {!isLoading && (
        <RecentSessionsList sessions={sessions} onSessionUpdated={handleSessionUpdated} />
      )}

      <p className="border-t border-[var(--structural-border)] pt-4 text-xs leading-5 text-[var(--text-muted)]">
        状态台只呈现当前可读取的证据；“需观察”可能代表直播间暂时安静，不等同于采集故障。
      </p>
    </div>
  );
}
